/**
 * PlanSimple — un schéma d'implantation, partagé par les clés et les archives.
 *
 * ─── CE QUE CE COMPOSANT EST, ET CE QU'IL N'EST PAS ─────────────────────────
 *
 * Décision d'Henrico le 29/07 : « un petit plan très simple, un carré avec des
 * cubes qu'on met à droite à gauche ». Ce n'est PAS un relevé métré, pas un
 * plan d'architecte : c'est un repère visuel — « l'armoire 3 est au fond à
 * droite », « la porte du local technique est côté cour ».
 *
 * ─── POURQUOI DES POURCENTAGES ──────────────────────────────────────────────
 *
 * Les positions sont stockées en pourcentage de la surface du plan, jamais en
 * pixels. Le même schéma doit rester juste sur un téléphone dans un grenier et
 * sur un écran de bureau. Des pixels auraient collé le dessin à la taille de
 * l'écran de celui qui l'a posé.
 *
 * ─── UN SEUL COMPOSANT, DEUX USAGES ─────────────────────────────────────────
 *
 * `archives_contenants.plan_x/y` et `batiment_portes.plan_x/y` portent le même
 * modèle depuis les migrations 274 et 276. Écrire deux plans aurait garanti
 * qu'ils divergent — et l'un des deux aurait fini par ne plus être maintenu.
 *
 * ─── ACCESSIBILITÉ ──────────────────────────────────────────────────────────
 *
 * Le glisser-déposer n'est jamais le SEUL moyen de placer un élément : les
 * flèches du clavier déplacent l'élément sélectionné. Un plan qu'on ne peut
 * pas manipuler au clavier serait inutilisable pour une partie des agents, et
 * hors RGAA.
 */

import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

export interface TraitPlan {
  id: string;
  x1: number; y1: number; x2: number; y2: number;
  type: string;
  libelle?: string | null;
}

export interface ElementPlan {
  id: string;
  /** Étiquette courte affichée dans le rectangle — « ARM-03 », « Porte nord ». */
  code: string;
  libelle: string;
  /** Position en % (0–100). `null` = pas encore posé sur le plan. */
  x: number | null;
  y: number | null;
  /** Signal secondaire : « 2/3 sorties », « 8,5 / 12 m ». */
  badge?: string | null;
  /** Attire l'œil : clé dehors, armoire qui déborde. */
  alerte?: boolean;
  /**
   * Encombrement RÉEL au sol, en mètres. Facultatif : sans lui, l'élément garde
   * une taille de symbole. Avec lui, et si le local est mesuré, il est dessiné
   * à l'échelle — une armoire de 2 m occupe deux fois la place d'une armoire
   * de 1 m, ce qui est tout l'intérêt d'un plan de local.
   */
  largeurReelleM?: number | null;
  profondeurReelleM?: number | null;
}

/** M23 — une intervention posée sur le plan : un ROND (les portes sont des rectangles). */
export interface PastillePlan {
  id: string; x: number; y: number; libelle: string;
  niveau: 'urgent' | 'encours' | 'neutre';
}

interface Props {
  elements: ElementPlan[];
  /** M23 — le plan RÉEL téléversé (image), affiché sous tout le reste. */
  fondUrl?: string | null;
  /** M23 — interventions à situer : des ronds, colorés selon l'urgence. */
  pastilles?: PastillePlan[];
  onClicPastille?: (id: string) => void;
  /**
   * M23 — un clic sur le FOND renvoie la position (en %). Sert à « situer une
   * intervention sur le plan ». Ignoré en mode tracé (le tracé a la priorité).
   */
  onClicPosition?: (x: number, y: number) => void;
  /** Consigne affichée au-dessus du plan pendant un placement. */
  consigne?: string | null;
  /**
   * ⚠️ CONSERVÉ POUR COMPATIBILITÉ, PLUS UTILISÉ POUR LA FORME DU DESSIN.
   *
   * La surface de dessin était auparavant mise au format du local
   * (`aspectRatio: largeur / hauteur`). Saisir un local de 1 m sur 20 donnait
   * une bande verticale interminable, et la page défilait sans fin — constaté
   * le 31/07 sur le plan des archives.
   *
   * Décision d'Henrico : **la surface reste un carré**, quoi qu'on saisisse.
   * Les dimensions du local ne servent plus qu'à l'ÉCHELLE, c'est-à-dire au
   * calcul — pas à la forme du cadre.
   */
  largeur?: number;
  hauteur?: number;
  /** Dimensions RÉELLES en mètres — donnent son échelle au quadrillage. */
  largeurM?: number | null;
  hauteurM?: number | null;
  traits?: TraitPlan[];
  modifiable: boolean;
  onDeplacer: (id: string, x: number, y: number) => void;
  onRetirer?: (id: string) => void;
  onClic?: (id: string) => void;
  /** Trace un trait. Sans ce rappel, le mode dessin n'est pas proposé. */
  onTracer?: (t: { x1: number; y1: number; x2: number; y2: number; type: string }) => void;
  onSupprimerTrait?: (id: string) => void;
  /** Message affiché quand rien n'est posé. */
  vide?: string;
}

/** Borne une valeur dans [0, 100] — un élément ne sort jamais du plan. */
const borne = (v: number) => Math.min(100, Math.max(0, v));

const TYPES_TRAIT = [
  { v: 'mur', l: 'Mur' },
  { v: 'cloison', l: 'Cloison' },
  { v: 'ouverture', l: 'Ouverture' },
  { v: 'cote', l: 'Cote' },
] as const;

export function PlanSimple({
  elements, largeur = 100, hauteur = 100, largeurM, hauteurM,
  traits = [], modifiable,
  onDeplacer, onRetirer, onClic, onTracer, onSupprimerTrait, vide,
  fondUrl, pastilles = [], onClicPastille, onClicPosition, consigne,
}: Props) {
  const zoneRef = useRef<HTMLDivElement | null>(null);
  const [saisi, setSaisi] = useState<string | null>(null);
  const [selection, setSelection] = useState<string | null>(null);
  // Mode dessin : on clique un point, puis un second, et le trait est posé.
  // Deux clics plutôt qu'un glisser : sur un téléphone, le glisser entre en
  // conflit avec le défilement de la page.
  const [modeTrace, setModeTrace] = useState<string | null>(null);
  const [depart, setDepart] = useState<{ x: number; y: number } | null>(null);

  /**
   * ─── LA TAILLE D'UN ÉLÉMENT SUR LE PLAN ─────────────────────────────────
   *
   * Le cadre fait 100 % de côté et représente `largeurM` × `hauteurM` mètres.
   * Un meuble de 1,20 m dans un local de 6 m occupe donc 20 % de la largeur.
   *
   * Renvoie `null` quand on ne peut pas être à l'échelle — local non mesuré, ou
   * meuble sans encombrement saisi. L'appelant retombe alors sur la taille de
   * symbole : mieux vaut un repère franchement schématique qu'une taille fausse
   * qu'on croirait juste.
   *
   * Un plancher de 4 % évite qu'une armoire étroite dans un grand local
   * devienne un point insaisissable à la souris.
   */
  function tailleReelle(el: ElementPlan): { w: number; h: number } | null {
    if (!largeurM || !hauteurM || largeurM <= 0 || hauteurM <= 0) return null;
    if (!el.largeurReelleM) return null;
    const w = Math.max(4, Math.min(100, (el.largeurReelleM / largeurM) * 100));
    const h = el.profondeurReelleM
      ? Math.max(4, Math.min(100, (el.profondeurReelleM / hauteurM) * 100))
      : Math.max(4, w / 3);   // sans profondeur : un meuble vu de dessus reste étroit
    return { w, h };
  }

  const poses = elements.filter((e) => e.x !== null && e.y !== null);
  const aPoser = elements.filter((e) => e.x === null || e.y === null);

  function positionDepuisEvenement(e: PointerEvent): { x: number; y: number } | null {
    const zone = zoneRef.current;
    if (!zone) return null;
    const r = zone.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return null;
    return {
      x: borne(((e.clientX - r.left) / r.width) * 100),
      y: borne(((e.clientY - r.top) / r.height) * 100),
    };
  }

  /** Un clic sur le fond : soit on pose un départ de trait, soit on le ferme. */
  function auClicFond(e: PointerEvent) {
    // M23 — hors mode tracé, un clic sur le fond renvoie la position.
    if (!modeTrace && onClicPosition) {
      if ((e.target as HTMLElement).closest('button')) return; // clic sur un élément
      const p = positionDepuisEvenement(e);
      if (p) onClicPosition(p.x, p.y);
      return;
    }
    if (!modeTrace || !onTracer) return;
    const p = positionDepuisEvenement(e);
    if (!p) return;
    if (!depart) { setDepart(p); return; }
    onTracer({ x1: depart.x, y1: depart.y, x2: p.x, y2: p.y, type: modeTrace });
    setDepart(null);
  }

  function auDeplacement(e: PointerEvent) {
    if (!saisi || !modifiable) return;
    const p = positionDepuisEvenement(e);
    if (p) onDeplacer(saisi, p.x, p.y);
  }

  function auClavier(e: KeyboardEvent, el: ElementPlan) {
    if (!modifiable || el.x === null || el.y === null) return;
    // Pas de glisser-déposer obligatoire : le clavier doit suffire (RGAA).
    const pas = e.shiftKey ? 10 : 2;
    const map: Record<string, [number, number]> = {
      ArrowLeft: [-pas, 0], ArrowRight: [pas, 0],
      ArrowUp: [0, -pas], ArrowDown: [0, pas],
    };
    const d = map[e.key];
    if (!d) return;
    e.preventDefault();
    onDeplacer(el.id, borne(el.x + d[0]), borne(el.y + d[1]));
  }

  return (
    <div className="space-y-3">
      {modifiable && onTracer && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            Tracer
          </span>
          {TYPES_TRAIT.map((t) => (
            <button
              key={t.v} type="button"
              onClick={() => {
                setModeTrace(modeTrace === t.v ? null : t.v);
                setDepart(null);
              }}
              className={[
                'rounded-r-pill border px-2.5 py-1 text-2xs',
                modeTrace === t.v
                  ? 'border-tech bg-tech-tint text-tech-fg'
                  : 'border-border bg-surface/60 text-ink-muted hover:text-ink',
              ].join(' ')}
            >
              {t.l}
            </button>
          ))}
          {modeTrace && (
            <span className="text-2xs text-ink-muted">
              {depart
                ? 'Cliquez le second point.'
                : 'Cliquez le premier point du trait.'}
            </span>
          )}
        </div>
      )}

      {consigne && (
        <p className="mb-2 rounded-r-md border border-tech/40 bg-tech-tint px-3 py-1.5 text-xs text-tech-fg" role="status">
          {consigne}
        </p>
      )}
      <div
        ref={zoneRef}
        onPointerMove={auDeplacement}
        onPointerUp={() => setSaisi(null)}
        onPointerLeave={() => setSaisi(null)}
        onClick={(e) => auClicFond(e as unknown as PointerEvent)}
        className="relative w-full overflow-hidden rounded-r-md border border-border bg-surface/40"
        style={{ aspectRatio: '1 / 1' }}
        role="group"
        aria-label="Plan d’implantation"
      >
        {/* M23 — le plan réel (évacuation, architecte, photo), sous tout le reste.
            object-contain : l'image n'est jamais déformée ; les positions en %
            se rapportent au carré, identique sur tous les écrans. */}
        {fondUrl && (
          <img src={fondUrl} alt="" aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain" />
        )}
        {/* Quadrillage discret : sans repère, on ne sait pas aligner. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'linear-gradient(to right, currentColor 1px, transparent 1px),'
              + 'linear-gradient(to bottom, currentColor 1px, transparent 1px)',
            backgroundSize: '10% 10%',
            color: 'var(--color-border, #94a3b8)',
          }}
        />

        {/* Les traits, sous les éléments : un mur ne masque pas une armoire. */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"
        >
          {traits.map((t) => (
            <line
              key={t.id}
              x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
              stroke="currentColor"
              className={t.type === 'ouverture' ? 'text-tech' : 'text-ink-muted'}
              strokeWidth={t.type === 'mur' ? 1.2 : 0.6}
              strokeDasharray={t.type === 'ouverture' ? '2 1.5'
                : t.type === 'cote' ? '1 1' : undefined}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {depart && (
            <circle cx={depart.x} cy={depart.y} r={1.2} className="fill-tech" />
          )}
        </svg>

        {poses.length === 0 && pastilles.length === 0 && !modeTrace && !fondUrl && (
          <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-xs text-ink-subtle">
            {vide ?? 'Rien n’est encore posé sur le plan.'}
          </p>
        )}

        {/* M23 — les interventions : des ronds (les portes sont des rectangles). */}
        {pastilles.map((pa) => (
          <button
            key={`pa-${pa.id}`}
            type="button"
            onClick={(e) => { e.stopPropagation(); onClicPastille?.(pa.id); }}
            title={pa.libelle}
            aria-label={`Intervention : ${pa.libelle}${pa.niveau === 'urgent' ? ' (urgente)' : ''}`}
            className={[
              'absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-soft',
              pa.niveau === 'urgent' ? 'bg-urgent' : pa.niveau === 'encours' ? 'bg-warning' : 'bg-ink-muted',
            ].join(' ')}
            style={{ left: `${pa.x}%`, top: `${pa.y}%` }}
          />
        ))}

        {poses.map((el) => (
          <button
            key={el.id}
            type="button"
            onPointerDown={() => modifiable && setSaisi(el.id)}
            onKeyDown={(e) => auClavier(e, el)}
            onFocus={() => setSelection(el.id)}
            onClick={() => onClic?.(el.id)}
            title={`${el.code} — ${el.libelle}${el.badge ? ` · ${el.badge}` : ''}`}
            aria-label={`${el.code}, ${el.libelle}${el.badge ? `, ${el.badge}` : ''}`}
            className={[
              'absolute -translate-x-1/2 -translate-y-1/2 rounded-r-md border px-2 py-1',
              'text-2xs leading-tight shadow-soft transition-shadow',
              modifiable ? 'cursor-move' : 'cursor-pointer',
              el.alerte
                ? 'border-urgent/50 bg-urgent-tint text-urgent'
                : 'border-tech/40 bg-tech-tint text-tech-fg',
              selection === el.id ? 'ring-2 ring-tech' : '',
            ].join(' ')}
            style={(() => {
              const t = tailleReelle(el);
              // À l'échelle : on impose la taille et on centre le rectangle sur
              // le point posé. Sinon, taille naturelle du contenu, comme avant.
              return t
                ? { left: `${el.x}%`, top: `${el.y}%`, width: `${t.w}%`, height: `${t.h}%` }
                : { left: `${el.x}%`, top: `${el.y}%` };
            })()}
          >
            <span className="block truncate font-medium">{el.code}</span>
            {el.badge && <span className="block truncate opacity-80">{el.badge}</span>}
          </button>
        ))}
      </div>

      {modifiable && (
        <p className="text-2xs text-ink-subtle">
          Faites glisser un élément pour le déplacer. Au clavier : sélectionnez-le
          puis utilisez les flèches — Maj pour aller plus vite.
          {largeurM && hauteurM ? (
            <>
              {' '}Échelle : le local mesure {largeurM} m sur {hauteurM} m, donc
              un carreau vaut environ {(largeurM / 10).toFixed(1)} m.
            </>
          ) : (
            <> Renseignez les dimensions du local pour afficher l’échelle.</>
          )}
        </p>
      )}

      {/* Les traits posés, avec de quoi les défaire — sans cette liste, un trait
          mal placé serait impossible à retirer (le SVG ne reçoit pas les clics,
          exprès, pour ne pas gêner le déplacement des éléments). */}
      {modifiable && onSupprimerTrait && traits.length > 0 && (
        <div>
          <p className="mb-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            Traits ({traits.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {traits.map((t, i) => (
              <button
                key={t.id} type="button"
                onClick={() => onSupprimerTrait(t.id)}
                title="Retirer ce trait"
                className="rounded-r-pill border border-border bg-surface/60 px-2.5 py-1 text-2xs text-ink-muted hover:text-urgent-fg"
              >
                {TYPES_TRAIT.find((x) => x.v === t.type)?.l ?? t.type} {i + 1} ✕
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ce qui n'est pas encore posé. Sans cette liste, un élément créé sans
          coordonnées serait invisible et on le croirait perdu. */}
      {aPoser.length > 0 && (
        <div>
          <p className="mb-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            Pas encore sur le plan ({aPoser.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {aPoser.map((el) => (
              <button
                key={el.id}
                type="button"
                disabled={!modifiable}
                onClick={() => onDeplacer(el.id, 50, 50)}
                title={modifiable ? 'Poser au centre du plan' : undefined}
                className="rounded-r-pill border border-border bg-surface/60 px-2.5 py-1 text-2xs text-ink-muted hover:text-ink disabled:opacity-60"
              >
                {el.code}
              </button>
            ))}
          </div>
        </div>
      )}

      {modifiable && onRetirer && selection && poses.some((p) => p.id === selection) && (
        <button
          type="button"
          onClick={() => { onRetirer(selection); setSelection(null); }}
          className="text-2xs text-ink-subtle underline hover:text-urgent-fg"
        >
          Retirer du plan l’élément sélectionné (il reste dans la liste)
        </button>
      )}
    </div>
  );
}
